

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select2OptionData } from 'ng-select2';
import { ToastrService } from 'ngx-toastr';
import { CreateResult } from '../models/create-result';
import { ParticipantViewModel } from '../models/participant-view-model';
import { ParticipantService } from '../services/participant.service';
import { AuthenticationService } from '../services/authentication.service';
import { UserDataStoreService } from '../services/user-data-store.service';
import { PermissionEnum } from '../services/guards/permissions';
import { MerchantService } from '../services/merchant.service';
import { ConfirmationDialogComponent } from '../partials/confirmation-dialog/confirmation-dialog.component';
import { CardAcceptorConfigService } from '../services/card-acceptor-config.service';
import { TerminalCapabilityService } from "../services/terminal-capability.service";
import { NgxUiLoaderService } from 'ngx-ui-loader';

@Component({
  selector: 'app-terminal-capability-mapping-view',
  templateUrl: './terminal-capability-mapping-view.component.html',
  styleUrls: ['./terminal-capability-mapping-view.component.scss']
})
export class TerminalCapabilityMappingViewComponent implements OnInit {

  public terminalCapabilities: Select2OptionData[] = [];
  public mapCustomTerminalCapabilities: Select2OptionData[];

  public participant: ParticipantViewModel;
  public terminalCapabilitiesByParticipant: any[] = [];

  public createResult: CreateResult;
  public canEdit: boolean;
  errorMessage: string;
  error: boolean;
  public selectedTerminalCapability: string;

  public select2options: any = {
    multiple: false,
    width: '100%',
    allowClear: true
  };

  constructor(private _modalService: NgbModal,
    private _cardAcceptorConfigService: CardAcceptorConfigService,
    private _participantService: ParticipantService,
    private _terminalCapabilityService: TerminalCapabilityService,
    private _merchantService: MerchantService,
    private _authenticationService: AuthenticationService,
    private _toastService: ToastrService,
    private _userDataStore: UserDataStoreService,
    private _route: ActivatedRoute,
    private _ngxUiLoaderService: NgxUiLoaderService) {

  }

  public async ngOnInit(): Promise<void> {
    this._route.params.subscribe(async params => {
      this.canEdit = this._userDataStore.hasRequiredPermissions([PermissionEnum.EditParticipantConfiguration], []);
      await this.initParticipant(params.id);
      this.terminalCapabilities = (await this._terminalCapabilityService.GetTerminalCapabilities()).map((x) => {
        return { id: x.capabilityProfileId.toString(), text: x.capabilityProfileName }
      });
      this.terminalCapabilitiesByParticipant = (await this._terminalCapabilityService.GetTerminalCapabilitiesForParticipant(this.participant.id)).map(p => ({
        terminalCapabilityId: p.capabilityProfileId,
        participantId: this.participant.id,
      }));
      this.filterTerminalCapabilities();
    });
  }

  public async initParticipant(id: number): Promise<void> {
    this.participant = await this._participantService.GetParticipant(id);
  }

  public filterTerminalCapabilities(): void {
    const filteredItems = this.terminalCapabilities.filter((item) => {
      if (this.terminalCapabilitiesByParticipant != null) {
        const index = this.terminalCapabilitiesByParticipant.findIndex(x => x.terminalCapabilityId.toString() === item.id);
        if (index > -1) {
          return false;
        }
      }
      return true;
    });
    this.mapCustomTerminalCapabilities = filteredItems;
  }

  public async save(): Promise<void> {

    if (!this.canEdit) {
      this._toastService.error('You do not have the required permissions to perform this action.', 'Error');
    } else {

      this.createResult = await this._terminalCapabilityService.DeleteParticipantTerminalCapabilitiesByParticipant(this.participant.id);

      if (this.createResult.success) {
        this.createResult = await this._terminalCapabilityService.UpdateParticipantTerminalCapabilities(this.terminalCapabilitiesByParticipant);
        if (this.createResult.success) {
          this._toastService.success(this.createResult.message, 'Success');
        } else {
          this._toastService.error(this.createResult.message, 'Error');
        }
      }
      else {
        this._toastService.error(this.createResult.message, 'Error');
      }
    }
  }


  public addTerminalCapabilitiesFields(): void {
    if (this.selectedTerminalCapability == null) {
      alert('Select a Terminal Capability');
    }
    else {
      this.terminalCapabilitiesByParticipant.push({
        terminalCapabilityId: parseInt(this.selectedTerminalCapability, 10),
        participantId: this.participant.id,
      });
      this.filterTerminalCapabilities();
    }
  }

  public deleteMappedField(terminalCapabilityId: string): void {

    const index = this.terminalCapabilitiesByParticipant.findIndex(x => x.terminalCapabilityId == terminalCapabilityId);

    if (index > -1) {
      this.terminalCapabilitiesByParticipant.splice(index, 1);
    }
    this.filterTerminalCapabilities();
  }

  public async determineDeleteDialog(terminalCapability: string) {

    this._ngxUiLoaderService.start();
    var cardAcceptors = await this._terminalCapabilityService.GetTerminalsByTerminalCapability(terminalCapability);
    this._ngxUiLoaderService.stop();

    if (cardAcceptors <= 0) {

      await this.openDeleteConfirmationDialog(terminalCapability);
    }
    else {
      await this.openUsedDeleteFailureConfirmationDialog(cardAcceptors);
    }
  }

  public async openDeleteConfirmationDialog(cardset: string) {

    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.showCancel = true;
    dialogRef.componentInstance.acceptButtonText = 'Yes';
    dialogRef.componentInstance.cancelButtonText = 'No'
    dialogRef.componentInstance.title = 'Terminal Capability Removal'
    dialogRef.componentInstance.message = 'Are you sure you want to delete the selected terminal capability from participant?'

    const result = await dialogRef.result;
    if (result) {
      await this.deleteMappedField(cardset);
    }
  }

  public async openUsedDeleteFailureConfirmationDialog(count: number) {
    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.acceptButtonText = 'OK'
    dialogRef.componentInstance.title = 'Terminal Capability Cannot Be Removed'
    dialogRef.componentInstance.message = `The terminal capability is currently in use by ${count} terminal(s) and cannot be deleted.`
  }
}

